import { match } from 'ts-pattern';

import { GetNotes } from '@/app/(rooms)/rooms/[videoCode]/components/Sidebar/data/Notes.query';
import { roomData } from '@/app/(rooms)/rooms/[videoCode]/roomData';

import type { SubscribeNotes } from '@/app/(rooms)/rooms/[videoCode]/components/Sidebar/data/Notes.subscription';
import type { CreateNote } from '@/app/[videoCode]/playback/data/CreateNote.mutation';
import type { DeleteNote } from '@/app/[videoCode]/playback/data/DeleteNote.mutation';
import type { Cache } from '@urql/exchange-graphcache';
import type { ResultOf, VariablesOf } from 'gql.tada';

export const noteMutationUpdates = {
  createAskableVideoRoomNote(result: ResultOf<typeof CreateNote>, args: VariablesOf<typeof CreateNote>, cache: Cache) {
    if (!roomData._room_id) throw new Error('Room ID is not set');

    cache.updateQuery<ResultOf<typeof GetNotes>, VariablesOf<typeof GetNotes>>(
      { query: GetNotes, variables: { code: args.input.code, _room_id: roomData._room_id } },
      data => {
        data?.roomNotes?.push(result.createAskableVideoRoomNote);
        return data;
      },
    );
  },

  deleteAskableVideoRoomNote(_result: ResultOf<typeof DeleteNote>, args: VariablesOf<typeof DeleteNote>, cache: Cache) {
    if (!roomData._room_id) throw new Error('Room ID is not set');

    cache.updateQuery<ResultOf<typeof GetNotes>, VariablesOf<typeof GetNotes>>(
      { query: GetNotes, variables: { code: args.input.code, _room_id: roomData._room_id } },
      data => ({ ...data, roomNotes: data?.roomNotes?.filter(note => note?._id !== args.input.id) ?? [] }),
    );
  },
};

export const noteSubscriptionUpdates = {
  videoRoomNote(result: ResultOf<typeof SubscribeNotes>, args: VariablesOf<typeof SubscribeNotes>, cache: Cache) {
    if (!roomData._room_id) throw new Error('Room ID is not set');

    cache.updateQuery<ResultOf<typeof GetNotes>, VariablesOf<typeof GetNotes>>(
      { query: GetNotes, variables: { code: args.filter.code, _room_id: roomData._room_id } },
      data => {
        const updateNote = result.videoRoomNote;

        // Skip self-originated events: a second cache write for our own note
        // clobbers the first.
        if (roomData._user_id === updateNote?.note?._user_id) return data;

        return match(updateNote)
          .with({ type: 'delete' }, note => {
            const roomNotes = data?.roomNotes?.filter(existing => existing?._id !== note?.note?._id);
            return roomNotes ? { ...data, roomNotes } : data;
          })
          .with({ type: 'create' }, note => ({ ...data, roomNotes: [...(data?.roomNotes ?? []), note.note] }))
          .with({ type: 'update' }, note => {
            // Match by the incoming note's id. The old predicate compared the
            // iteratee to itself, so it always hit index 0 and overwrote the
            // wrong note.
            const index = data?.roomNotes?.findIndex(existing => existing?._id === note?.note?._id);
            if (typeof index === 'number' && index !== -1 && data?.roomNotes?.[index] && note?.note) {
              data.roomNotes[index] = note.note;
            }
            return data;
          })
          .otherwise(() => data);
      },
    );
  },
};
